// src/hooks/usePlanejamentoAtendimentos.js
import { useMemo } from "react";

import { useAuth } from "../contexts/AuthContext";
import { useEmpresa } from "../contexts/EmpresaContext";
import { resolveEmpresaSelecionada } from "../utils/resolveEmpresaSelecionada";
import {
  criarAtendimento,
  criarSessao,
  listarAtendimentosPorClienteCompetencia,
  listarSessoesDoAtendimento,
  listarSessoesPorTecnicoPeriodo,
  listarSessoesPorPeriodo,
  atualizarSessao,
  listarItensSessao,
  listarItensSessaoResumo,
  contarItensSessao,
  adicionarItensSessao,
  removerItensSessao,
  excluirAtendimento,
  excluirSessao,
} from "../services/planejamentoAtendimentosApi";

function assertScope(scope) {
  if (!scope?.grupoId) throw new Error("grupoId nao informado");
}

function resolveEmpresaId(scope, options) {
  return options?.empresaId || scope?.empresaId || null;
}

// Hook para o modulo Planejamento (Atendimento/Visitas e Sessoes).
export function usePlanejamentoAtendimentos() {
  const { usuario } = useAuth();
  const { empresaAtual } = useEmpresa();

  const scope = useMemo(
    () => resolveEmpresaSelecionada(usuario, empresaAtual),
    [usuario, empresaAtual]
  );

  const criarAtendimentoScoped = async (clienteId, contratoTipo, competencia, progresso, options = {}) => {
    assertScope(scope);
    const empresaId = resolveEmpresaId(scope, options);
    if (!empresaId) throw new Error("empresaId nao informado");
    return criarAtendimento({
      grupoId: scope.grupoId,
      empresaId,
      clienteId,
      contratoTipo,
      competencia,
      progresso,
      setorId: options?.setorId || null,
      academiaId: options?.academiaId || null,
    });
  };

  const listarAtendimentosScoped = async (clienteId, competencia, options = {}) => {
    assertScope(scope);
    const empresaId = resolveEmpresaId(scope, options);
    const allowGlobal = options?.allowGlobal === true;
    if (!empresaId && !allowGlobal) throw new Error("empresaId nao informado");
    return listarAtendimentosPorClienteCompetencia({
      grupoId: scope.grupoId,
      empresaId: empresaId || null,
      clienteId: clienteId || null,
      competencia,
    });
  };

  const criarSessaoScoped = async (atendimentoId, tecnicoId, inicioEm, fimEm, status, observacoes, options = {}) => {
    assertScope(scope);
    const empresaId = resolveEmpresaId(scope, options);
    if (!empresaId) throw new Error("empresaId nao informado");
    return criarSessao({
      grupoId: scope.grupoId,
      empresaId,
      atendimentoId,
      tecnicoId,
      inicioEm,
      fimEm,
      status,
      observacoes,
      financeiro: options?.financeiro || null,
    });
  };

  const listarSessoesScoped = async (atendimentoId, options = {}) => {
    assertScope(scope);
    const empresaId = resolveEmpresaId(scope, options);
    if (!empresaId) throw new Error("empresaId nao informado");
    return listarSessoesDoAtendimento({
      grupoId: scope.grupoId,
      empresaId,
      atendimentoId,
    });
  };

  const listarSessoesPorTecnicoPeriodoScoped = async (tecnicoId, inicioEm, fimEm) => {
    assertScope(scope);
    return listarSessoesPorTecnicoPeriodo({
      grupoId: scope.grupoId,
      empresaId: scope.empresaId,
      tecnicoId,
      inicioEm,
      fimEm,
    });
  };

  const listarSessoesPorPeriodoScoped = async (inicioEm, fimEm, options = {}) => {
    assertScope(scope);
    const empresaId = resolveEmpresaId(scope, options);
    return listarSessoesPorPeriodo({
      grupoId: scope.grupoId,
      empresaId: empresaId || null,
      tecnicoId: options?.tecnicoId || null,
      inicioEm,
      fimEm,
    });
  };

  const atualizarSessaoScoped = async (sessaoId, inicioEm, fimEm, status, observacoes, tecnicoId, financeiro) => {
    assertScope(scope);
    return atualizarSessao({
      sessaoId,
      inicioEm,
      fimEm,
      status,
      observacoes,
      tecnicoId,
      financeiro,
    });
  };

  const listarItensSessaoScoped = async (sessaoId, options = {}) => {
    assertScope(scope);
    const empresaId = resolveEmpresaId(scope, options);
    if (!empresaId) throw new Error("empresaId nao informado");
    return listarItensSessao({
      grupoId: scope.grupoId,
      empresaId,
      sessaoId,
    });
  };

  const listarItensSessaoResumoScoped = async (sessaoId, limite, options = {}) => {
    assertScope(scope);
    const empresaId = resolveEmpresaId(scope, options);
    if (!empresaId) throw new Error("empresaId nao informado");
    return listarItensSessaoResumo({
      grupoId: scope.grupoId,
      empresaId,
      sessaoId,
      limite,
    });
  };

  const contarItensSessaoScoped = async (sessaoId, options = {}) => {
    assertScope(scope);
    const empresaId = resolveEmpresaId(scope, options);
    if (!empresaId) throw new Error("empresaId nao informado");
    return contarItensSessao({
      grupoId: scope.grupoId,
      empresaId,
      sessaoId,
    });
  };

  const adicionarItensSessaoScoped = async (atendimentoId, sessaoId, clienteId, itens, options = {}) => {
    assertScope(scope);
    const empresaId = resolveEmpresaId(scope, options);
    if (!empresaId) throw new Error("empresaId nao informado");
    return adicionarItensSessao({
      grupoId: scope.grupoId,
      empresaId,
      atendimentoId,
      sessaoId,
      clienteId,
      itens,
    });
  };

  const removerItensSessaoScoped = async (itensIds) => {
    assertScope(scope);
    return removerItensSessao({ itensIds });
  };

  const excluirAtendimentoScoped = async (atendimentoId, options = {}) => {
    assertScope(scope);
    const empresaId = resolveEmpresaId(scope, options);
    return excluirAtendimento({
      grupoId: scope.grupoId,
      empresaId: empresaId || null,
      atendimentoId,
    });
  };

  const excluirSessaoScoped = async (sessaoId, options = {}) => {
    assertScope(scope);
    const empresaId = resolveEmpresaId(scope, options);
    return excluirSessao({
      grupoId: scope.grupoId,
      empresaId: empresaId || null,
      sessaoId,
    });
  };

  return {
    scope,
    criarAtendimento: criarAtendimentoScoped,
    listarAtendimentosPorClienteCompetencia: listarAtendimentosScoped,
    criarSessao: criarSessaoScoped,
    listarSessoesDoAtendimento: listarSessoesScoped,
    listarSessoesPorTecnicoPeriodo: listarSessoesPorTecnicoPeriodoScoped,
    listarSessoesPorPeriodo: listarSessoesPorPeriodoScoped,
    atualizarSessao: atualizarSessaoScoped,
    listarItensSessao: listarItensSessaoScoped,
    listarItensSessaoResumo: listarItensSessaoResumoScoped,
    contarItensSessao: contarItensSessaoScoped,
    adicionarItensSessao: adicionarItensSessaoScoped,
    removerItensSessao: removerItensSessaoScoped,
    excluirAtendimento: excluirAtendimentoScoped,
    excluirSessao: excluirSessaoScoped,
  };
}
